import type React from 'react';
import { memo, useRef } from 'react';

import { Branding, Count, Icon, Image, Notification, Wrapper } from './wrapper';

const fallbackImageUrl = '/images/placeholders/placeholder-64x64.jpg';

interface MediaPreviewImageProps {
  src: string;
  srcSet?: string;
  alt: string;
  contain?: boolean;
  noReferrer?: boolean;
}

interface MediaPreviewBrandingProps {
  src: string;
  srcSet?: string;
  alt: string;
}

export interface MediaPreviewProps {
  image?: MediaPreviewImageProps;
  additionalCount?: number;
  size?: 'large' | 'small' | 'fullWidth';
  tone?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'error'
    | 'errorStrong'
    | 'info'
    | 'neutral'
    | 'transparent'
    | 'outline';
  icon?: React.ReactNode;
  branding?: MediaPreviewBrandingProps;
  notification?: React.ReactNode;
}

const MediaPreview = ({
  image,
  additionalCount,
  size,
  tone,
  icon,
  branding,
  notification,
}: MediaPreviewProps) => {
  const ref = useRef(false);
  return (
    <Wrapper $size={size} $tone={tone || 'neutral'}>
      {image && (
        <Image
          src={image.src}
          srcSet={image.srcSet}
          alt={image.alt}
          onError={(e) => {
            if (ref.current) return;
            ref.current = true;
            e.currentTarget.src = fallbackImageUrl;
          }}
          $contain={image.contain}
          referrerPolicy={image.noReferrer ? 'no-referrer' : undefined}
          draggable="false"
        />
      )}
      {additionalCount && <Count>+{additionalCount}</Count>}
      {icon && <Icon>{icon}</Icon>}
      {branding && <Branding src={branding.src} srcSet={branding.srcSet} alt={branding.alt} />}
      {notification && <Notification>{notification}</Notification>}
    </Wrapper>
  );
};

export default memo(MediaPreview);
